// Dialog bazowy (D-1..D-3): backdrop --overlay-backdrop, focus-trap, Esc / klik w tło,
// fokus wraca na wyzwalacz, przewijanie strony zatrzymane. Warstwa --z-modal.
import { useCallback, useEffect, useRef, type ReactNode } from "react";
import { createPortal } from "react-dom";

// Blokada przewijania strony liczona na sztuki. Gdy nad jednym oknem stanie drugie (np. pytanie
// „porzucić zmiany?" nad edycją numeru), zamknięcie wierzchniego nie może odblokować strony,
// która wciąż jest przykryta tym pod spodem.
let scrollLocks = 0;
function lockBodyScroll(): () => void {
  scrollLocks += 1;
  document.body.style.overflow = "hidden";
  return () => {
    scrollLocks = Math.max(0, scrollLocks - 1);
    if (scrollLocks === 0) document.body.style.overflow = "";
  };
}

function getFocusable(root: HTMLElement): HTMLElement[] {
  return Array.from(
    root.querySelectorAll<HTMLElement>(
      'button, [href], input, select, textarea, [tabindex]:not([tabindex="-1"])',
    ),
  ).filter((el) => !el.hasAttribute("disabled") && el.offsetParent !== null);
}

export function Modal({
  open,
  onClose,
  labelledBy,
  panelClassName,
  initialFocusSelector,
  children,
}: {
  open: boolean;
  onClose: () => void;
  labelledBy?: string;
  panelClassName?: string;
  initialFocusSelector?: string;
  children: ReactNode;
}) {
  const panelRef = useRef<HTMLDivElement>(null);
  const triggerRef = useRef<HTMLElement | null>(null);

  // onClose jest u wołających funkcją tworzoną przy każdym renderze. Gdyby wisiał w zależnościach
  // efektu, efekt sprzątałby się i uruchamiał po KAŻDYM naciśnięciu klawisza - a razem z nim
  // ustawianie fokusu startowego. W oknie z jednym polem to zjada pierwszy znak, w oknie
  // z formularzem nie da się nic wpisać. Dlatego zamknięcie trzymamy w referencji, a efekt
  // od fokusu zależy wyłącznie od tego, czy okno jest otwarte.
  const onCloseRef = useRef(onClose);
  onCloseRef.current = onClose;
  const requestClose = useCallback(() => onCloseRef.current(), []);

  // Otwarcie: zapamiętaj wyzwalacz, zatrzymaj przewijanie strony, ustaw fokus startowy.
  useEffect(() => {
    if (!open) return;
    triggerRef.current = document.activeElement as HTMLElement | null;
    const releaseScroll = lockBodyScroll();

    const panel = panelRef.current;
    if (panel) {
      const initial = initialFocusSelector
        ? panel.querySelector<HTMLElement>(initialFocusSelector)
        : getFocusable(panel)[0];
      (initial ?? panel).focus();
    }
    return () => {
      releaseScroll();
      triggerRef.current?.focus?.();
    };
  }, [open, initialFocusSelector]);

  // Esc zamyka, Tab krąży wewnątrz okna.
  useEffect(() => {
    if (!open) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") {
        e.preventDefault();
        requestClose();
        return;
      }
      const panel = panelRef.current;
      if (e.key !== "Tab" || !panel) return;
      const items = getFocusable(panel);
      if (!items.length) return;
      const first = items[0];
      const last = items[items.length - 1];
      if (e.shiftKey && document.activeElement === first) {
        e.preventDefault();
        last.focus();
      } else if (!e.shiftKey && document.activeElement === last) {
        e.preventDefault();
        first.focus();
      }
    };
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, [open, requestClose]);

  if (!open) return null;

  return createPortal(
    <div
      className="modal"
      role="dialog"
      aria-modal="true"
      aria-labelledby={labelledBy}
      onMouseDown={(e) => {
        if (e.target === e.currentTarget) onClose();
      }}
    >
      <div
        ref={panelRef}
        className={panelClassName ?? "modal__panel"}
        tabIndex={-1}
        onMouseDown={(e) => e.stopPropagation()}
      >
        {children}
      </div>
    </div>,
    document.body,
  );
}
